import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Loader2, ShieldAlert, KeyRound, ScrollText, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { adminAction } from "@/lib/adminApi";
import { errorToast } from "@/components/ui/sonner";
import { supabase } from "@/integrations/supabase/client";
import { AdminPageHeader, AdminSearch, AdminSection, AdminPanel } from "./AdminUI";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export function AdminImpersonateTab() {
  const [email, setEmail] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);

  const impersonate = useMutation({
    mutationFn: async () => {
      const data = await adminAction("impersonate", { target_email: email });
      return data;
    },
    onSuccess: async (data) => {
      if (!data.token_hash) {
        localStorage.removeItem("impersonation"); sessionStorage.removeItem("impersonation");
        errorToast("Error", "Could not generate session link.");
        return;
      }
      const expiresAt = Date.now() + (data.expires_in_seconds || 3600) * 1000;
      const payload = JSON.stringify({ active: true, userName: data.user_name, userId: data.user_id, expiresAt });
      localStorage.setItem("impersonation", payload);
      sessionStorage.setItem("impersonation", payload);
      const { error } = await supabase.auth.verifyOtp({
        token_hash: data.token_hash,
        type: "magiclink",
      });
      if (error) {
        localStorage.removeItem("impersonation"); sessionStorage.removeItem("impersonation");
        errorToast("Error", error.message || "Failed to start impersonation session.");
        return;
      }
      window.location.href = "/";
    },
    onError: (e: Error) => {
      localStorage.removeItem("impersonation"); sessionStorage.removeItem("impersonation");
      errorToast("Error", e.message);
    },
  });

  return (
    <div className="space-y-10">
      <AdminPageHeader
        eyebrow="Support"
        title="Impersonate"
        description="Sign in as another user to debug their account. Every session is logged and time-limited."
      />

      <div
        className="flex items-start gap-3 rounded-[4px] px-5 py-4"
        style={{
          background: "rgba(220,38,38,0.05)",
          border: "1px solid rgba(220,38,38,0.18)",
        }}
      >
        <ShieldAlert className="w-4 h-4 mt-0.5 text-[#dc2626] flex-shrink-0" />
        <div>
          <p className="text-[11px] uppercase tracking-[0.22em] text-[#dc2626]">
            Real session, real consequences
          </p>
          <p className="text-[12.5px] text-muted-foreground mt-1 leading-relaxed max-w-2xl">
            Impersonation creates a real session as the target user. Anything you do
            is recorded against their account. Always exit when done.
          </p>
        </div>
      </div>

      <AdminSection
        title="Start session"
        description="Generates a one-time link consumed in-browser — the token never appears in history."
      >
        <div className="flex gap-2 max-w-xl">
          <AdminSearch
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && email.trim()) setConfirmOpen(true); }}
            placeholder="Target user email…"
            containerClassName="flex-1"
          />
          <Button
            onClick={() => setConfirmOpen(true)}
            disabled={impersonate.isPending || !email.trim()}
            className="h-9 px-4 text-[11.5px] uppercase tracking-[0.18em] rounded-[3px] text-white"
            style={{ background: "#dc2626" }}
          >
            {impersonate.isPending ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              "Impersonate"
            )}
          </Button>
        </div>
      </AdminSection>

      <AdminSection title="How it works">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <ProcessCard
            icon={KeyRound}
            step="01"
            title="Token issued"
            body="A single-use magic link is generated server-side and never written to the URL bar."
          />
          <ProcessCard
            icon={Clock}
            step="02"
            title="Session expires"
            body="Sessions last roughly one hour. Refresh tokens are not extended on impersonated accounts."
          />
          <ProcessCard
            icon={ScrollText}
            step="03"
            title="Fully audited"
            body="Every impersonation start, exit, and action lands in the audit log against your admin identity."
          />
        </div>
      </AdminSection>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm impersonation</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to sign in as <strong>{email}</strong>? All actions will be logged to the audit trail.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { setConfirmOpen(false); impersonate.mutate(); }}
              className="bg-[#dc2626] hover:bg-[#dc2626]/90 text-white"
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ProcessCard({
  icon: Icon,
  step,
  title,
  body,
}: {
  icon: React.ElementType;
  step: string;
  title: string;
  body: string;
}) {
  return (
    <AdminPanel padded>
      <div className="flex items-center justify-between mb-5">
        <div
          className="text-[10px] uppercase tracking-[0.32em]"
          style={{ color: "#c2541a" }}
        >
          Step {step}
        </div>
        <Icon className="w-3.5 h-3.5 text-muted-foreground" />
      </div>
      <h4
        className="text-[18px] text-foreground"
        style={{
          fontFamily: "Outfit, sans-serif",
          fontWeight: 300,
          letterSpacing: "-0.01em",
        }}
      >
        {title}
      </h4>
      <p className="text-[12.5px] text-muted-foreground mt-2 leading-relaxed">{body}</p>
    </AdminPanel>
  );
}
